"use client";
import PrimaryBtn from "@/components/PrimaryBtn";
import { Icons, Images } from "@assets/assets";
import Navigation from "@/HomeFeatures/Navigation";
import React, { useEffect, useRef } from "react";
// PERF: GSAP + ScrollTrigger are loaded lazily via ensureGsap() so they stay out
// of the initial route bundle and registerPlugin runs after mount instead of at
// module evaluation. The `gsap` instance is cached in gsapRef for the synchronous
// helper/handlers below. Animation behavior is unchanged.
import type GsapNS from "gsap";
import { ensureGsap } from "@/lib/EnsureGsap";
import Image from "next/image";
import { useScrollToCareerForm } from "@/lib/ScrollUtils";

export default function CareerHero() {

  const teamContainerRef = useRef<HTMLDivElement | null>(null);
  const teamWrapperRef = useRef<HTMLDivElement | null>(null);
  const pageRootRef = useRef<HTMLDivElement | null>(null);
  const loopRef = useRef<ReturnType<(typeof GsapNS)["timeline"]> | null>(null);
  const gsapRef = useRef<typeof GsapNS | null>(null);

  const horizontalLoop = (items: HTMLElement[], config: any) => {
    const gsap = gsapRef.current!;
    items = gsap.utils.toArray(items);
    config = config || {};
    let tl = gsap.timeline({
      repeat: config.repeat,
      paused: config.paused,
      defaults: { ease: "none" },
      onReverseComplete: () => {
        tl.totalTime(tl.rawTime() + tl.duration() * 100);
      },
    }),
      length = items.length,
      startX = items[0].offsetLeft,
      widths: number[] = [],
      xPercents: number[] = [],
      pixelsPerSecond = (config.speed || 1) * 100,
      snap =
        config.snap === false
          ? (v: any) => v
          : gsap.utils.snap(config.snap || 1);

    gsap.set(items, {
      xPercent: (i, el) => {
        let w = (widths[i] = parseFloat(
          gsap.getProperty(el, "width", "px") as string,
        ));
        xPercents[i] = snap(
          (parseFloat(gsap.getProperty(el, "x", "px") as string) / w) * 100 +
          (gsap.getProperty(el, "xPercent") as number),
        );
        return xPercents[i];
      },
    });
    gsap.set(items, { x: 0 });

    let totalWidth =
      items[length - 1].offsetLeft +
      (xPercents[length - 1] / 100) * widths[length - 1] -
      startX +
      items[length - 1].offsetWidth *
      (gsap.getProperty(items[length - 1], "scaleX") as number) +
      (parseFloat(config.paddingRight) || 0);
    for (let i = 0; i < length; i++) {
      let item = items[i],
        curX = (xPercents[i] / 100) * widths[i],
        distanceToStart = item.offsetLeft - startX + curX,
        distanceToLoop =
          distanceToStart +
          widths[i] * (gsap.getProperty(item, "scaleX") as number);
      tl.to(
        item,
        {
          xPercent: snap(((curX - distanceToLoop) / widths[i]) * 100),
          duration: distanceToLoop / pixelsPerSecond,
        },
        0,
      ).fromTo(
        item,
        {
          xPercent: snap(
            ((curX - distanceToLoop + totalWidth) / widths[i]) * 100,
          ),
        },
        {
          xPercent: snap(((curX + totalWidth) / widths[i]) * 100),
          duration:
            (curX + totalWidth - (curX - distanceToLoop + totalWidth)) /
            pixelsPerSecond,
          immediateRender: false,
        },
        distanceToLoop / pixelsPerSecond,
      );
    }
    return tl;
  };

  useEffect(() => {
    if (typeof window !== "undefined" && window.innerWidth < 1024) return;

    const container = teamContainerRef.current;
    const wrapper = teamWrapperRef.current;
    if (!container || !wrapper) return;

    const images = Array.from(
      container.querySelectorAll<HTMLImageElement>(".team-item"),
    );
    if (images.length === 0) return;

    let cancelled = false;
    let heroMaster: ReturnType<(typeof GsapNS)["timeline"]> | null = null;
    let imageMaster: ReturnType<(typeof GsapNS)["timeline"]> | null = null;
    let io: IntersectionObserver | null = null;
    let cleanupST: (() => void) | null = null;

    const waitForImages = (imgs: HTMLImageElement[]) =>
      Promise.all(
        imgs?.map((img) => {
          if (img.complete) return Promise.resolve();
          return new Promise<void>((resolve) => {
            const timeout = setTimeout(resolve, 1000);
            const done = () => {
              clearTimeout(timeout);
              resolve();
            };
            img.addEventListener("load", done, { once: true });
            img.addEventListener("error", done, { once: true });
          });
        }),
      );

    ensureGsap().then(({ gsap, ScrollTrigger }) => {
      if (cancelled) return;
      gsapRef.current = gsap;
      cleanupST = () => ScrollTrigger.getAll().forEach((t) => t.kill());

      const root = pageRootRef.current;
      const heroElements = root
        ? Array.from(root.querySelectorAll<HTMLElement>("[data-hero]"))
        : [];

      // LCP PROTECTION: the hero heading/copy render at full opacity in the
      // initial HTML (this is the LCP candidate on mobile). Never set their
      // opacity to 0/0.01 here — that re-hides already-painted content and
      // pushes Chrome's LCP timing out to whenever this fade-back finishes,
      // which is what was happening before. Only animate a transform.
      if (heroElements.length) {
        gsap.set(heroElements, { y: 40 });
        heroElements.forEach((el) =>
          el.classList.remove("opacity-0", "transform-y-[100px]"),
        );

        heroMaster = gsap.timeline();
        heroMaster.to(
          heroElements,
          {
            y: 0,
            ease: "power2.out",
            duration: 0.8,
            stagger: 0.1,
          }
        );
      }

      waitForImages(images).then(() => {
        if (cancelled) return;

        const wrapperRect = wrapper.getBoundingClientRect();
        const wrapperCenterX = wrapperRect.left + wrapperRect.width / 2;

        const deltas = images?.map((img) => {
          const rect = img.getBoundingClientRect();
          return rect.left + rect.width / 2 - wrapperCenterX;
        });

        const rotations = images?.map(
          () => (Math.random() < 0.5 ? -1 : 1) * (Math.random() * 5),
        );

        gsap.set(images, {
          x: (index: number) => -deltas[index],
          y: "-20vh",
          rotation: (index: number) => rotations[index],
          boxShadow: "2px 2px 8px 2px rgb(0 0 0 / 17%)",
          visibility: "visible",
        });

        imageMaster = gsap.timeline();
        imageMaster.to(
          images,
          {
            x: 0,
            y: 0,
            rotation: 0,
            boxShadow: "0px 0px 0px 0px rgba(0,0,0,0)",
            ease: "power3.out",
            duration: 1.2,
            stagger: 0.05,
            onComplete: () => {
              gsap.set(images, { clearProps: "boxShadow" });
              loopRef.current = horizontalLoop(images as HTMLElement[], {
                speed: 0.5,
                repeat: -1,
                paddingRight: 15,
                paused: true,
              });

              // TBT/INP PROTECTION: Pause the loop when out of viewport
              io = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                  if (entry.isIntersecting) {
                    loopRef.current?.play();
                  } else {
                    loopRef.current?.pause();
                  }
                });
              });
              io.observe(wrapper);
            },
          },
          0.4,
        );
      });
    });

    return () => {
      cancelled = true;
      if (heroMaster) heroMaster.kill();
      if (imageMaster) imageMaster.kill();
      if (loopRef.current) loopRef.current.kill();
      if (io) io.disconnect();
      cleanupST?.();
    };
  }, []);

  const handleMouseEnter = () => {
    const gsap = gsapRef.current;
    if (gsap && loopRef.current)
      gsap.to(loopRef.current, {
        timeScale: 0,
        duration: 0.6,
        ease: "power2.out",
      });
  };

  const handleMouseLeave = () => {
    const gsap = gsapRef.current;
    if (gsap && loopRef.current)
      gsap.to(loopRef.current, {
        timeScale: 1,
        duration: 0.6,
        ease: "power2.in",
      });
  };
  const handleSmoothScroll = useScrollToCareerForm();
  return (
    <section className="relative h-[max-content] flex flex-col max-w-[100vw] overflow-hidden">
      <div className="relative z-10 w-full">
        <div
          ref={pageRootRef}
          className="w-full h-full bg-[#F3F7FE] flex flex-col min-h-[80vh] pb-12 lg:pb-[100px]"
        >
          <Navigation dark={false} />
          <h1
            data-hero
            className="self-stretch pt-[60px] lg:pt-[75px] px-6 lg:px-0 mx-auto text-center max-w-[22ch] lg:max-w-[18ch] text-black text-4xl sm:text-5xl lg:text-[length:var(--service-banner-title)] font-normal clash-font capitalize leading-tight lg:leading-[100%]"
          >
            Be a Part of<span className="text-[#225BD4]"> Innovation</span>
          </h1>
          <p
            data-hero
            className="justify-start text-[#636364] px-6 lg:px-0 mx-auto text-base sm:text-lg lg:text-[length:var(--banner-para)] max-w-[540px] lg:max-w-[90%] text-center font-normal leading-[150%] tracking-tight py-4"
          >
            We are always on the hunt for talented professionals who bring
            genuine passion in what they do, excel in their expertise, and are
            committed to grow their knowledge and career with us.
          </p>
          <div
            data-hero
            className="flex flex-col items-center gap-4 px-6 lg:px-0"
          >
            <PrimaryBtn
              styleClass="mx-auto mt-6 lg:mt-[30px]"
              dark={false}
              text="See Current Opportunities"
              iconSrc={Icons?.chevron}
              onClick={() => {
                handleSmoothScroll();
              }}
            />
          </div>

          <div
            ref={teamWrapperRef}
            className="hidden w-full mt-[50px] overflow-visible lg:block cursor-pointer"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <div
              ref={teamContainerRef}
              className="team-container w-[max-content] flex gap-[15px] items-end"
            >
              {[...Array(2)]?.map((_, setIdx) => (
                <React.Fragment key={setIdx}>
                  {Array.from({ length: 7 })?.map((_, index) => (
                    <Image
                      key={`${setIdx}-${index}`}
                      src={
                        Images.career[
                        `image${index + 1}` as keyof typeof Images.career
                        ] as unknown as string
                      }
                      width={250}
                      height={150}
                      alt="career-item"
                      className="team-item flex-shrink-0 object-contain"
                      sizes="(max-width: 768px) 100vw, (max-width: 1536px) 50vw, 700px"
                    />
                  ))}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
